import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { ThemeToggle } from "../theme/ThemeToggle";
import type { AdminLayoutProps, NavItem } from "./types";

const STORAGE_KEY = "pentarab-admin-sidebar-collapsed";

function readStoredCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-2 rounded text-sm transition-colors ${
    isActive
      ? "bg-sidebar-accent text-sidebar-foreground"
      : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground"
  }`;

function NavList({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, end, icon }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={linkClass}
          title={collapsed ? label : undefined}
        >
          {collapsed ? (
            <>
              {icon ? (
                <span className="flex justify-center w-full">{icon}</span>
              ) : (
                <span className="flex justify-center w-full font-mono text-xs">
                  {label.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="sr-only">{label}</span>
            </>
          ) : icon ? (
            <span className="inline-flex items-center gap-2">
              {icon}
              {label}
            </span>
          ) : (
            label
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function Footer({
  sidebarFooter,
  onLogout,
  collapsed,
  onToggleCollapsed,
}: {
  sidebarFooter?: AdminLayoutProps["sidebarFooter"];
  onLogout: AdminLayoutProps["onLogout"];
  collapsed: boolean;
  onToggleCollapsed?: () => void;
}) {
  return (
    <div className="mt-auto flex flex-col gap-2 text-xs text-sidebar-muted">
      {sidebarFooter}
      {onToggleCollapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="flex w-full items-center justify-center gap-2 text-sm text-sidebar-muted hover:text-sidebar-foreground"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <PanelLeftOpen size={14} aria-hidden="true" />
          ) : (
            <>
              <PanelLeftClose size={14} aria-hidden="true" />
              <span>Collapse</span>
            </>
          )}
        </button>
      )}
      <ThemeToggle compact={collapsed} />
      {collapsed ? (
        <button
          type="button"
          onClick={() => void onLogout()}
          className="flex w-full items-center justify-center text-sidebar-muted hover:text-sidebar-foreground"
          aria-label="Logout"
          title="Logout"
        >
          <LogOut size={14} aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void onLogout()}
          className="text-left text-sidebar-muted hover:text-sidebar-foreground underline"
        >
          Logout
        </button>
      )}
    </div>
  );
}

function renderBrandCollapsed(
  brand: AdminLayoutProps["brand"],
  brandCollapsed: AdminLayoutProps["brandCollapsed"],
) {
  if (brandCollapsed !== undefined) return brandCollapsed;
  if (typeof brand === "string") return brand.slice(0, 1).toUpperCase();
  return null;
}

export function AdminLayout({
  brand,
  brandCollapsed,
  navItems,
  onLogout,
  sidebarFooter,
  children,
}: AdminLayoutProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => readStoredCollapsed());

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(STORAGE_KEY, String(next));
        } catch {
          // ignore — Safari private mode / quota exceeded / Lockdown Mode
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const desktopSidebarClass = `hidden md:flex flex-col h-screen sticky top-0 bg-sidebar text-sidebar-foreground transition-all duration-200 ${
    collapsed ? "w-14 px-2 py-4" : "w-56 p-4"
  }`;

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className={desktopSidebarClass}>
        <Link
          to="/"
          className={`text-lg font-semibold mb-6 text-sidebar-foreground truncate ${
            collapsed ? "flex items-center justify-center" : ""
          }`}
          title={collapsed && typeof brand === "string" ? brand : undefined}
        >
          {collapsed ? renderBrandCollapsed(brand, brandCollapsed) : brand}
        </Link>
        <NavList items={navItems} collapsed={collapsed} />
        <Footer
          sidebarFooter={sidebarFooter}
          onLogout={onLogout}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </aside>

      <div className="md:hidden fixed inset-x-0 top-0 z-30 bg-sidebar border-b border-sidebar-hover">
        <div className="flex items-center gap-2 px-3 py-2 text-sidebar-foreground">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="p-2 -ml-2 text-sidebar-muted hover:text-sidebar-foreground"
            aria-label="Open menu"
            aria-expanded={open}
          >
            <Menu size={20} />
          </button>
          <Link to="/" className="font-semibold text-sidebar-foreground">
            {brand}
          </Link>
          <button
            type="button"
            onClick={() => void onLogout()}
            className="ml-auto text-xs text-sidebar-muted hover:text-sidebar-foreground"
            aria-label="Logout"
          >
            Logout
          </button>
        </div>
      </div>

      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-sidebar text-sidebar-foreground p-4 flex flex-col transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="text-lg font-semibold text-sidebar-foreground">
            {brand}
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1 text-sidebar-muted hover:text-sidebar-foreground"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>
        <NavList items={navItems} collapsed={false} />
        <Footer sidebarFooter={sidebarFooter} onLogout={onLogout} collapsed={false} />
      </aside>

      <main className="flex-1 p-4 md:p-5 max-w-none mx-auto pt-14 md:pt-5 w-full">
        {children}
      </main>
    </div>
  );
}
